"use client";

import { useState, useCallback } from "react";

type UseDragDropPropsT = {
  onFileSelect: (file: File | File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
};

/**
 * Custom hook for drag and drop file handling
 *
 * Provides drag and drop functionality with proper event handling
 * and visual feedback. Handles all drag events and file extraction.
 *
 * @param props - Hook configuration
 * @returns Drag handlers and state
 *
 * @example
 * ```typescript
 * const {
 *   isDragOver,
 *   dragHandlers
 * } = useDragDrop({
 *   onFileSelect: handleFileUpload,
 *   disabled: isUploading
 * });
 *
 * <div {...dragHandlers}>
 *   {isDragOver && <DragOverlay />}
 * </div>
 * ```
 */
export function useDragDrop({
  onFileSelect,
  disabled = false,
  multiple = false,
}: UseDragDropPropsT) {
  const [isDragOver, setIsDragOver] = useState(false);

  // Handle file drop
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        if (multiple) {
          onFileSelect(Array.from(files));
        } else {
          onFileSelect(files[0]);
        }
      }
    },
    [onFileSelect, disabled, multiple]
  );

  // Handle drag over (required to allow drop)
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  // Handle drag enter
  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  // Handle drag leave with proper boundary detection
  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // Only set dragOver to false if we're leaving the container itself
      // This prevents flickering when dragging over child elements
      if (event.currentTarget.contains(event.relatedTarget as Node)) {
        return;
      }

      setIsDragOver(false);
    },
    []
  );

  // Return drag handlers as a single object for easy spreading
  const dragHandlers = {
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
  };

  return {
    isDragOver,
    dragHandlers,
  };
}
